import React, { useState, useRef, useCallback, useEffect } from 'react';
import './LiveTranscriptionRecorder.css';
import { useTranscriptionLimit } from '../hooks/useTranscriptionLimit';
import Icon from './Icons';
import { debugLog } from '../utils/debug';

const RECONNECT_INTERVAL_MS = 5000;
const RECONNECT_TIMEOUT_MS = 60000;

/**
 * LiveTranscriptionRecorder
 *
 * Records audio in 500ms chunks and streams them to the server via WebSocket.
 * Transcription segments arrive incrementally and are displayed live.
 * If the connection drops during recording, it buffers audio chunks and
 * attempts to reconnect for up to 60 seconds before giving up.
 */
const LiveTranscriptionRecorder = ({
  onTranscriptUpdate,
  onSessionComplete,
  onError,
  isAuthenticated = false,
  onLoginPrompt,
  autoStart = false
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [liveSegments, setLiveSegments] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [latency, setLatency] = useState(null);
  const [volumeLevel, setVolumeLevel] = useState(0);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [reconnectCountdown, setReconnectCountdown] = useState(null);

  const { checkLimit, LimitAlert } = useTranscriptionLimit();

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const sessionIdRef = useRef(null);
  const timerRef = useRef(null);
  const chunkIndexRef = useRef(0);
  const recordingStartTimeRef = useRef(null);
  const chunkTimestampsRef = useRef([]);
  const pausedAtRef = useRef(null);
  const totalPausedMsRef = useRef(0);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const wsRef = useRef(null);

  // Refs that mirror state so closures always see the latest value
  const isRecordingRef = useRef(false);
  const isReconnectingRef = useRef(false);
  const liveSegmentsRef = useRef([]);
  const onTranscriptUpdateRef = useRef(onTranscriptUpdate);

  // Reconnection
  const offlineChunkBufferRef = useRef([]);
  const reconnectRetryTimerRef = useRef(null);
  const reconnectCountdownTimerRef = useRef(null);
  const reconnectDeadlineRef = useRef(null);

  useEffect(() => { onTranscriptUpdateRef.current = onTranscriptUpdate; }, [onTranscriptUpdate]);
  useEffect(() => { isRecordingRef.current = isRecording; }, [isRecording]);

  // Recording duration counter — paused during reconnection
  useEffect(() => {
    if (isRecording && !isPaused && !isReconnecting) {
      timerRef.current = setInterval(() => setRecordingTime(prev => prev + 1), 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [isRecording, isPaused, isReconnecting]);

  // Warn before page unload during an active recording
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isRecording) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isRecording]);

  const formatTime = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds) % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  const formatTimestamp = useCallback((seconds) => {
    if (seconds == null) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds) % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Continuously reads frequency data and updates the volume level indicator
  const monitorVolume = useCallback(() => {
    if (!analyserRef.current) return;
    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);

    const tick = () => {
      if (!analyserRef.current) return;
      try {
        analyserRef.current.getByteFrequencyData(dataArray);
        const rms = Math.sqrt(dataArray.reduce((sum, v) => sum + v * v, 0) / dataArray.length);
        setVolumeLevel(Math.min(100, (rms / 255) * 100 * 3.0));
        animationFrameRef.current = requestAnimationFrame(tick);
      } catch (err) {
        console.error('[Volume] Error reading audio data:', err);
      }
    };

    tick();
  }, []);

  // Stops volume animation and closes the AudioContext
  const stopAudioContext = useCallback(async () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      await audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setVolumeLevel(0);
  }, []);

  // Release audio resources on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Cancels all reconnect timers and clears countdown display
  const clearReconnectTimers = useCallback(() => {
    clearInterval(reconnectRetryTimerRef.current);
    clearInterval(reconnectCountdownTimerRef.current);
    reconnectRetryTimerRef.current = null;
    reconnectCountdownTimerRef.current = null;
    reconnectDeadlineRef.current = null;
    setReconnectCountdown(null);
  }, []);

  // Tears down the recording session; preserves transcript segments for display
  const cleanupRecordingState = useCallback(() => {
    // Clear reconnect timers directly (avoids circular dep with clearReconnectTimers)
    clearInterval(reconnectRetryTimerRef.current);
    clearInterval(reconnectCountdownTimerRef.current);
    reconnectRetryTimerRef.current = null;
    reconnectCountdownTimerRef.current = null;
    reconnectDeadlineRef.current = null;
    setReconnectCountdown(null);

    setIsRecording(false);
    setIsConnecting(false);
    setIsPaused(false);
    setIsProcessing(false);
    setIsReconnecting(false);
    isReconnectingRef.current = false;

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch (e) {}
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setVolumeLevel(0);

    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      wsRef.current.close();
    }

    sessionIdRef.current = null;
    chunkIndexRef.current = 0;
    recordingStartTimeRef.current = null;
    chunkTimestampsRef.current = [];
    pausedAtRef.current = null;
    totalPausedMsRef.current = 0;
    offlineChunkBufferRef.current = [];
  }, []);

  // Sends a single audio chunk via WebSocket.
  // If the socket is not open and a reconnect is in progress, the chunk is buffered instead.
  const sendAudioChunk = useCallback(async (audioBlob, chunkIndex, offsetMs) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      if (isReconnectingRef.current) {
        offlineChunkBufferRef.current.push({ blob: audioBlob, chunkIndex, offsetMs });
        debugLog('[WebSocket] Buffered chunk during reconnect:', chunkIndex);
      }
      return;
    }

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          if (isReconnectingRef.current) {
            offlineChunkBufferRef.current.push({ blob: audioBlob, chunkIndex, offsetMs });
          }
          resolve();
          return;
        }
        try {
          wsRef.current.send(JSON.stringify({
            type: 'audio_chunk',
            data: reader.result.split(',')[1],
            chunkIndex,
            offsetMs
          }));
          debugLog('[WebSocket] Sent chunk:', chunkIndex, 'size:', audioBlob.size);
        } catch (err) {
          console.error('[WebSocket] Error sending chunk:', err);
        }
        resolve();
      };
      reader.onerror = () => resolve();
      reader.readAsDataURL(audioBlob);
    });
  }, []);

  // Replays all buffered chunks over the newly-established WebSocket session
  const flushOfflineBuffer = useCallback(async () => {
    const buffer = offlineChunkBufferRef.current;
    offlineChunkBufferRef.current = [];
    if (buffer.length === 0) return;
    debugLog('[WebSocket] Flushing', buffer.length, 'buffered chunks after reconnect');
    for (const { blob, chunkIndex, offsetMs } of buffer) {
      await sendAudioChunk(blob, chunkIndex, offsetMs);
    }
  }, [sendAudioChunk]);

  // Constructs the WebSocket URL, attaching the auth token when available
  const buildWsUrl = useCallback(() => {
    const token = localStorage.getItem('authToken');
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const base = `${protocol}://${window.location.host}/ws/live-transcribe`;
    return token ? `${base}?token=${encodeURIComponent(token)}` : base;
  }, []);

  // Keeps the MediaRecorder running but buffers chunks; retries the WebSocket
  // every 5 seconds for up to 60 seconds. On success, flushes the buffer and
  // resumes the live session. On timeout, stops the recording with an error.
  const initiateReconnection = useCallback(() => {
    if (isReconnectingRef.current) return;

    debugLog('[WebSocket] Connection dropped during recording, initiating reconnect');
    isReconnectingRef.current = true;
    setIsReconnecting(true);
    offlineChunkBufferRef.current = [];
    reconnectDeadlineRef.current = Date.now() + RECONNECT_TIMEOUT_MS;

    const remaining = () => Math.max(0, Math.ceil((reconnectDeadlineRef.current - Date.now()) / 1000));
    setReconnectCountdown(remaining());

    reconnectCountdownTimerRef.current = setInterval(() => {
      const secs = remaining();
      setReconnectCountdown(secs);
      if (secs <= 0) {
        clearInterval(reconnectCountdownTimerRef.current);
        clearInterval(reconnectRetryTimerRef.current);
        cleanupRecordingState();
        setError('Could not restore the connection after 60 seconds. Recording has been stopped.');
      }
    }, 1000);

    const tryReconnect = () => {
      if (!isReconnectingRef.current) return;
      if (Date.now() >= reconnectDeadlineRef.current) return;

      debugLog('[WebSocket] Attempting reconnect...');
      const ws = new WebSocket(buildWsUrl());

      ws.onopen = () => {
        const preferences = JSON.parse(localStorage.getItem('userPreferences') || '{}');
        ws.send(JSON.stringify({
          type: 'start',
          language: 'en',
          maxSilenceInterval: preferences.maxSilenceInterval || 1.0
        }));
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type !== 'session_started') return;

          debugLog('[WebSocket] Reconnect session started:', msg.sessionId);
          wsRef.current = ws;
          sessionIdRef.current = msg.sessionId;

          clearInterval(reconnectRetryTimerRef.current);
          clearInterval(reconnectCountdownTimerRef.current);
          reconnectRetryTimerRef.current = null;
          reconnectCountdownTimerRef.current = null;
          reconnectDeadlineRef.current = null;
          setReconnectCountdown(null);

          isReconnectingRef.current = false;
          setIsReconnecting(false);

          if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
            mediaRecorderRef.current.resume();
          }

          if (analyserRef.current) monitorVolume();

          flushOfflineBuffer();
        } catch (err) {
          console.error('[WebSocket] Reconnect message error:', err);
        }
      };

      ws.onerror = () => {
        debugLog('[WebSocket] Reconnect attempt failed');
        ws.close();
      };

      ws.onclose = () => {};
    };

    tryReconnect();
    reconnectRetryTimerRef.current = setInterval(tryReconnect, RECONNECT_INTERVAL_MS);
  }, [buildWsUrl, cleanupRecordingState, flushOfflineBuffer, monitorVolume]);

  // Starts the full recording session: opens WebSocket, acquires microphone,
  // then begins MediaRecorder once the server confirms the session
  const startLiveRecording = useCallback(async () => {
    // Define MediaRecorder startup here so the onmessage handler below can reference it
    const startMediaRecorder = async () => {
      try {
        const supportedTypes = [
          'audio/webm;codecs=opus',
          'audio/webm',
          'audio/ogg;codecs=opus',
          'audio/mp4',
          'audio/mpeg'
        ];
        const mimeType = supportedTypes.find(t => MediaRecorder.isTypeSupported(t));
        if (!mimeType) throw new Error('No supported audio format found for your browser');

        const mediaRecorder = new MediaRecorder(streamRef.current, { mimeType });
        mediaRecorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = async (event) => {
          if (event.data.size === 0) return;
          const offsetMs = Date.now() - recordingStartTimeRef.current - totalPausedMsRef.current;
          chunkTimestampsRef.current.push({ index: chunkIndexRef.current, sentAt: Date.now() });
          await sendAudioChunk(event.data, chunkIndexRef.current, offsetMs);
          chunkIndexRef.current++;
        };

        recordingStartTimeRef.current = Date.now();
        mediaRecorder.start(500);

        setIsRecording(true);
        setIsConnecting(false);
        setIsPaused(false);

        if (analyserRef.current) monitorVolume();
        debugLog('[WebSocket] Recording started');
      } catch (err) {
        console.error('[WebSocket] Failed to start MediaRecorder:', err);
        setError('Failed to start recording. Please try again.');
        if (onError) onError(err);
        wsRef.current?.close();
      }
    };

    try {
      setError(null);
      setIsConnecting(true);
      setLiveSegments([]);
      liveSegmentsRef.current = [];
      setRecordingTime(0);
      chunkIndexRef.current = 0;
      chunkTimestampsRef.current = [];
      pausedAtRef.current = null;
      totalPausedMsRef.current = 0;

      if (!navigator.mediaDevices?.getUserMedia) {
        setError('Your browser does not support audio recording. Please use a modern browser.');
        setIsConnecting(false);
        return;
      }
      if (!window.WebSocket) {
        setError('Your browser does not support WebSocket connections. Please use a modern browser.');
        setIsConnecting(false);
        return;
      }
      if (!window.MediaRecorder) {
        setError('Your browser does not support MediaRecorder. Please use Chrome, Firefox, or Safari.');
        setIsConnecting(false);
        return;
      }

      // Start WebSocket and request microphone access in parallel
      wsRef.current = new WebSocket(buildWsUrl());

      let micStream, canProceed;
      try {
        [micStream, canProceed] = await Promise.all([
          navigator.mediaDevices.getUserMedia({
            audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, sampleRate: 44100 }
          }),
          checkLimit()
        ]);
      } catch (err) {
        setIsConnecting(false);
        if (wsRef.current) {
          wsRef.current.close();
          wsRef.current = null;
        }
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          setError('Microphone access denied. Please allow microphone permissions in your browser settings.');
        } else if (err.name === 'NotFoundError') {
          setError('No microphone found. Please connect a microphone and try again.');
        } else {
          setError('Failed to access microphone. Please check your browser settings and try again.');
        }
        if (onError) onError(err);
        return;
      }

      if (!canProceed) {
        setIsConnecting(false);
        micStream.getTracks().forEach(t => t.stop());
        if (wsRef.current) {
          wsRef.current.close();
          wsRef.current = null;
        }
        return;
      }

      streamRef.current = micStream;

      // Set up AudioContext for the volume meter
      try {
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
          await audioContextRef.current.close().catch(() => {});
        }
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        if (audioContextRef.current.state === 'suspended') {
          await audioContextRef.current.resume();
        }
        const source = audioContextRef.current.createMediaStreamSource(micStream);
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 512;
        analyserRef.current.smoothingTimeConstant = 0.1;
        source.connect(analyserRef.current);
      } catch (err) {
        console.error('[Volume] Could not set up volume monitoring:', err);
      }

      const connectionTimeout = setTimeout(() => {
        if (wsRef.current && wsRef.current.readyState !== WebSocket.OPEN) {
          wsRef.current.close();
          setIsConnecting(false);
          setError('Connection timed out. The server may be down or unreachable. Please try again.');
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
            streamRef.current = null;
          }
        }
      }, 10000);

      wsRef.current.onopen = () => {
        clearTimeout(connectionTimeout);
        const preferences = JSON.parse(localStorage.getItem('userPreferences') || '{}');
        wsRef.current.send(JSON.stringify({
          type: 'start',
          language: 'en',
          maxSilenceInterval: preferences.maxSilenceInterval || 1.0
        }));
        debugLog('[WebSocket] Connected, sent start message');
      };

      wsRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          debugLog('[WebSocket] Received:', message.type);

          if (message.type === 'session_started') {
            sessionIdRef.current = message.sessionId;
            startMediaRecorder();

          } else if (message.type === 'transcription') {
            const newSegments = message.segments || [];
            liveSegmentsRef.current = [...liveSegmentsRef.current, ...newSegments];
            setLiveSegments([...liveSegmentsRef.current]);
            if (onTranscriptUpdateRef.current && liveSegmentsRef.current.length > 0) {
              onTranscriptUpdateRef.current(liveSegmentsRef.current);
            }
            const chunkInfo = chunkTimestampsRef.current.find(c => c.index === message.chunkIndex);
            if (chunkInfo) setLatency(Date.now() - chunkInfo.sentAt);

          } else if (message.type === 'session_complete') {
            const serverSegments = message.transcript?.data?.segments || [];
            const finalSegments = serverSegments.length > 0 ? serverSegments : liveSegmentsRef.current;

            setIsRecording(false);
            setIsPaused(false);
            setIsProcessing(false);
            setLiveSegments([]);
            liveSegmentsRef.current = [];

            if (onSessionComplete) {
              if (message.transcript?.id) {
                onSessionComplete(message.transcript, finalSegments);
              } else {
                console.error('[WebSocket] session_complete received without valid transcript:', message);
                onSessionComplete(null, finalSegments);
              }
            }

            sessionIdRef.current = null;
            chunkIndexRef.current = 0;
            recordingStartTimeRef.current = null;
            chunkTimestampsRef.current = [];
            pausedAtRef.current = null;
            totalPausedMsRef.current = 0;

          } else if (message.type === 'error') {
            console.error('[WebSocket] Server error:', message.message);
            setError(message.message || 'The server reported an error. Please try again.');
            setIsProcessing(false);
          }
        } catch (err) {
          console.error('[WebSocket] Error parsing message:', err);
        }
      };

      wsRef.current.onerror = (err) => {
        console.error('[WebSocket] Connection error:', err);
        clearTimeout(connectionTimeout);
        if (isRecordingRef.current && !isReconnectingRef.current) {
          initiateReconnection();
        } else if (!isRecordingRef.current) {
          setIsConnecting(false);
          setError('Connection failed. The server may be down. Please try again.');
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
            streamRef.current = null;
          }
        }
      };

      wsRef.current.onclose = (event) => {
        debugLog('[WebSocket] Closed:', event.code, event.reason);
        clearTimeout(connectionTimeout);

        if (event.code === 4401) {
          setError('Session expired. Please log in again to continue.');
          if (onLoginPrompt) onLoginPrompt();
          if (isRecordingRef.current) cleanupRecordingState();
          return;
        }

        if (isRecordingRef.current && !isReconnectingRef.current && (event.code === 1006 || !event.wasClean)) {
          initiateReconnection();
        }
      };

    } catch (err) {
      console.error('[LiveTranscription] Unexpected error starting recording:', err);
      setIsConnecting(false);
      setError('Failed to start live transcription. Please try again.');
      if (onError) onError(err);
    }
  }, [onError, monitorVolume, checkLimit, buildWsUrl, sendAudioChunk, initiateReconnection, cleanupRecordingState]);

  // Toggles between paused and recording states
  const togglePause = useCallback(async () => {
    if (!mediaRecorderRef.current || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    try {
      if (isPaused) {
        setIsPaused(false);
        if (pausedAtRef.current) {
          totalPausedMsRef.current += Date.now() - pausedAtRef.current;
          pausedAtRef.current = null;
        }
        if (mediaRecorderRef.current.state === 'paused') mediaRecorderRef.current.resume();
        if (analyserRef.current) monitorVolume();
        wsRef.current.send(JSON.stringify({ type: 'resume' }));
        debugLog('[WebSocket] Resumed recording');
      } else {
        setIsPaused(true);
        pausedAtRef.current = Date.now();
        if (mediaRecorderRef.current.state === 'recording') mediaRecorderRef.current.pause();
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        setVolumeLevel(0);
        wsRef.current.send(JSON.stringify({ type: 'pause' }));
        debugLog('[WebSocket] Paused recording');
      }
    } catch (err) {
      console.error('[WebSocket] Error toggling pause:', err);
    }
  }, [isPaused, monitorVolume]);

  // Stops MediaRecorder, sends final stop message, and waits for session_complete
  const stopLiveRecording = useCallback(async () => {
    if (!mediaRecorderRef.current) {
      setIsRecording(false);
      setIsPaused(false);
      return;
    }

    clearReconnectTimers();
    isReconnectingRef.current = false;
    setIsReconnecting(false);

    setIsProcessing(true);
    setIsRecording(false);
    setIsPaused(false);

    try {
      await stopAudioContext();

      await new Promise((resolve) => {
        if (mediaRecorderRef.current) {
          mediaRecorderRef.current.onstop = resolve;
          mediaRecorderRef.current.stop();
        } else {
          resolve();
        }
      });

      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }

      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        const preferences = JSON.parse(localStorage.getItem('userPreferences') || '{}');
        wsRef.current.send(JSON.stringify({
          type: 'stop',
          transcriptName: `Live Transcript ${new Date().toLocaleString()}`,
          enableDiarization: preferences.enableDiarization === true
        }));
        debugLog('[WebSocket] Sent stop message, waiting for session_complete');

        // Safety timeout: if server doesn't respond within 30s, unblock the UI
        setTimeout(() => {
          setIsProcessing(current => {
            if (current) {
              setError('Transcription response timed out. Please check your history to see if it was saved.');
              return false;
            }
            return current;
          });
        }, 30000);
      } else {
        setIsProcessing(false);
        if (offlineChunkBufferRef.current.length > 0) {
          setError('Recording stopped while offline. Some audio may not have been transcribed.');
        }
      }
    } catch (err) {
      console.error('[WebSocket] Error stopping recording:', err);
      setError('Failed to stop transcription. Please try again.');
      if (onError) onError(err);
    }
  }, [onError, clearReconnectTimers, stopAudioContext]);

  // Auto-start recording if requested by the parent component
  const autoStartTriggeredRef = useRef(false);
  useEffect(() => {
    if (autoStart && !autoStartTriggeredRef.current && !isRecording) {
      autoStartTriggeredRef.current = true;
      const timer = setTimeout(() => startLiveRecording(), 50);
      return () => clearTimeout(timer);
    }
  }, [autoStart, isRecording, startLiveRecording]);

  // Close the WebSocket cleanly on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      debugLog('[LiveTranscription] Segments updated:', liveSegments.length);
    }
  }, [liveSegments]);

  return (
    <div className="live-transcription-recorder">
      <LimitAlert />

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      {isReconnecting && (
        <div className="reconnecting-banner">
          <span className="reconnecting-spinner" />
          <span>
            Connection lost — attempting to reconnect
            {reconnectCountdown != null ? ` (${reconnectCountdown}s remaining)` : ''}...
          </span>
        </div>
      )}

      <div className="recorder-controls">
        {isConnecting && !isRecording && (
          <div className="recording-status-bar">
            <div className="status-left">
              <span className="recording-indicator"></span>
              <span className="recording-status-text">Connecting...</span>
            </div>
          </div>
        )}

        {isRecording && (
          <div className="recording-status-bar">
            <div className="status-left">
              <span className={`recording-indicator ${!isPaused && !isReconnecting ? 'active' : ''}`}></span>
              <span className="recording-status-text">
                {isReconnecting ? 'Reconnecting...' : isPaused ? 'Paused' : 'Live'}
              </span>
              <span className="time">{formatTime(recordingTime)}</span>
              {!isPaused && !isReconnecting && (
                <div className="volume-leds">
                  {[20, 40, 60, 80, 100].map((threshold, i) => (
                    <span
                      key={i}
                      className={`volume-led ${volumeLevel >= threshold ? 'active' : ''} ${i >= 3 ? 'high' : ''}`}
                    />
                  ))}
                </div>
              )}
              {latency && !isReconnecting && (
                <div className="latency-indicator">
                  <span className={`latency-value ${latency < 500 ? 'good' : latency < 1000 ? 'moderate' : 'poor'}`}>
                    {latency}ms
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="main-controls">
          {isRecording && (
            <>
              <button
                className="control-btn pause-btn"
                onClick={togglePause}
                disabled={isReconnecting}
                title={isPaused ? 'Resume' : 'Pause'}
              >
                <Icon name={isPaused ? 'play' : 'pause'} size={24} />
              </button>

              <button
                className="control-btn stop-btn"
                onClick={stopLiveRecording}
                title="Stop and Save"
              >
                <Icon name="stop" size={24} />
              </button>
            </>
          )}
        </div>
      </div>

      {isRecording && !isReconnecting && liveSegments.length === 0 && (
        <div
          className="live-waiting-message"
          style={{ fontStyle: 'italic', color: 'var(--color-text-secondary)', textAlign: 'center', padding: '1rem', fontSize: '0.9rem' }}
        >
          Listening... speak to see transcription appear below
        </div>
      )}

      {isProcessing && (
        <div className="live-processing-indicator">
          <div className="processing-spinner-small"></div>
          <span>Processing transcription...</span>
        </div>
      )}
    </div>
  );
};

export default LiveTranscriptionRecorder;
